import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { DataSourceLoadOptions } from '@sleed/bizcommerce-api';
import { productsApi } from '~/lib/api/client';
import { tokenToProductRow, type ProductGridRow } from '~/lib/fixtures/demo-products';
import { applyFilters, type ProductFilters } from '~/lib/products/product-filters';
import { applySort, type SortDir } from '~/lib/grid/sort';

/** Saved-filter chip identifiers (mirrors the approved HTML chip row). */
export type ProductChip = 'outOfStock' | 'hidden' | 'newReleases' | 'featured' | 'noImage';

export interface ProductsListParams {
	skip?: number;
	take?: number;
	/** Debounced free-text search (name / SKU / barcode). */
	search?: string;
	/** Active saved-filter chip, if any. */
	chip?: ProductChip | null;
	/** Advanced filters from the Filters side-sheet, if any. */
	filters?: ProductFilters | null;
	/** Active sort selector (a ProductGridProjection property name), or null for server default. */
	sortSelector?: string | null;
	/** Active sort direction (ignored when sortSelector is null). */
	sortDir?: SortDir;
}

export interface ProductsListResult {
	items: ProductGridRow[];
	totalCount: number;
}

export const productKeys = {
	all: ['products'] as const,
	list: (params: unknown) => ['products', 'list', params] as const,
	counts: () => ['products', 'counts'] as const,
	detail: (id: string | number) => ['products', 'detail', String(id)] as const
};

/** ISO date `n` days before today (used by the "New releases" chip → ReleaseDateFrom). */
function isoDaysAgo(days: number): string {
	const d = new Date();
	d.setDate(d.getDate() - days);
	return d.toISOString().slice(0, 10);
}

/**
 * Translates an active chip into the `getAllGrid` custom filter fields that BIND through
 * the generated client (scalar query params read by DataSourceLoadOptionsHttpBinder):
 *   - outOfStock  → quantityTo = 0           (binds: scalar int)
 *   - hidden      → isVisible  = 2 (tri-state "No")  (binds: scalar int)
 *   - newReleases → releaseDateFrom = today-90d (binds: scalar date string)
 *   - noImage     → hasImage = 2 (HasImage.No)  (binds: scalar int)
 *   - featured    → isFeatured = 1 (tri-state "Yes")  (binds: scalar int)
 *
 * Every chip uses a scalar custom field applied server-side to the product query (in both the
 * sorted and unsorted GetAllGridV2 paths). Featured deliberately does NOT use `loadOptions.filter`:
 * the generated client serialises `filter` in a shape the DevExtreme parser mangles, and the V2
 * sorted path bypasses `loadOptions.Filter` entirely — so a `filter`-based chip 500s when unsorted
 * and is silently ignored when a column sort is active. `isFeatured` mirrors the `isVisible` field.
 */
function applyChip(loadOptions: DataSourceLoadOptions, chip: ProductChip | null | undefined): void {
	if (!chip) return;
	const mutable = loadOptions as Record<string, unknown>;
	switch (chip) {
		case 'outOfStock':
			mutable.quantityTo = 0;
			break;
		case 'hidden':
			mutable.isVisible = 2;
			break;
		case 'newReleases':
			mutable.releaseDateFrom = isoDaysAgo(90);
			break;
		case 'noImage':
			mutable.hasImage = 2;
			break;
		case 'featured':
			// Scalar tri-state field (1 = featured), read by DataSourceLoadOptionsHttpBinder.
			mutable.isFeatured = 1;
			break;
	}
}

/**
 * Server-paged products list via `getAllGrid` (DevExtreme loadOptions envelope). Mirrors the
 * legacy admin's `/products/getallgrid` call. Reads `res.data.data` + `res.data.totalCount`.
 *
 * `keepPreviousData` keeps the previous page visible while the next page/filter loads, so the
 * grid doesn't flash empty on every page/search change.
 */
export function useProductsList(params: ProductsListParams = {}) {
	const { skip = 0, take = 25, search = '', chip = null, filters = null, sortSelector = null, sortDir = 'asc' } = params;
	return useQuery<ProductsListResult>({
		queryKey: productKeys.list({ skip, take, search, chip, filters, sortSelector, sortDir }),
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const loadOptions: DataSourceLoadOptions = { skip, take, requireTotalCount: true };
			if (search.trim()) loadOptions.searchValue = search.trim();
			// Chip first, then advanced filters. The sheet supersedes a chip on overlapping
			// fields (e.g. both touch isVisible/hasImage) because applyFilters runs last.
			applyChip(loadOptions, chip);
			if (filters) applyFilters(loadOptions, filters);
			if (sortSelector) applySort(loadOptions, sortSelector, sortDir);

			// getAllGridV2 = the admin-v2 grid endpoint: same shape as the legacy getAllGrid but applies
			// server-side column sorting to the SQL projection. The legacy getAllGrid is left untouched.
			const res = await productsApi.getAllGridV2(loadOptions);
			const items = (res.data.data ?? []).map(tokenToProductRow);
			return { items, totalCount: res.data.totalCount ?? 0 };
		}
	});
}
